import React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Receipt, Calendar, User } from "lucide-react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { getCustomerDisplayName } from "@/utils/customerDisplayHelpers";
import InvoiceStatusBadge from "./components/InvoiceStatusBadge";
import InvoiceActions from "./components/InvoiceActions";
import { getInvoiceTypeBadge } from "./utils/invoiceTypeBadge";

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'partial';

interface Invoice {
  id: string;
  invoice_number?: string | null;
  title?: string;
  status: string;
  invoice_type?: string | null;
  percentage_of_estimate?: number | null;
  total_amount?: number | null;
  created_at: string;
  due_date?: string | null;
  customers?: {
    id: string;
    name: string;
    last_name: string;
    business_name?: string | null;
    customer_type?: string;
  } | null;
}

interface InvoiceCardProps {
  invoices: Invoice[];
}

/**
 * InvoiceCard - Card/grid view for invoices list
 * 
 * Displays invoices as visual cards showing:
 * - Invoice number and title
 * - Status badge
 * - Customer name
 * - Total amount
 * - Invoice type
 * - Created and due dates
 * - Actions menu
 */
const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoices }) => {

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {invoices.map((invoice) => (
        <Card key={invoice.id} className="hover:shadow-md transition-shadow overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 min-w-0">
                <Receipt className="h-5 w-5 text-primary flex-shrink-0" />
                <Link 
                  to={`/invoices/${invoice.id}`} 
                  className="font-semibold font-montserrat hover:underline truncate"
                >
                  {(invoice.title?.replace(/^Invoice for\s*/i, '') || 'Untitled') + ' INV'}
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-mono truncate">
                    {invoice.invoice_number || 'INV-PENDING'}
                  </span>
                  <InvoiceStatusBadge 
                    status={invoice.status as InvoiceStatus} 
                    dueDate={invoice.due_date}
                    size="sm"
                  />
                </div>
                <InvoiceActions
                  invoiceId={invoice.id}
                  currentStatus={invoice.status}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Customer and Type */}
            <div className="flex items-center justify-between mb-3">
              {invoice.customers && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{getCustomerDisplayName(invoice.customers)}</span>
                </div>
              )}
              {getInvoiceTypeBadge(invoice.invoice_type || 'full', invoice.percentage_of_estimate)}
            </div>
            
            {/* Amount */}
            <div className="text-lg font-semibold mb-3">
              {formatCurrency(invoice.total_amount || 0)}
            </div>
            
            {/* Dates */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Created: {format(new Date(invoice.created_at), 'dd MMMM yyyy')}
              </div>
              {invoice.due_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span className={
                    new Date(invoice.due_date) < new Date() && invoice.status !== 'paid'
                      ? 'text-destructive font-medium' 
                      : ''
                  }>
                    Due: {format(new Date(invoice.due_date), 'dd MMMM yyyy')}
                  </span>
                </div>
              )}
            </div>

          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InvoiceCard;
